import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Globe, Loader2, Pencil, Save } from 'lucide-react';
import {
  enregistrerReseauxSponsor,
  enregistrerSiteWebSponsor,
  fetchMonDossierSponsor,
} from '../services/api';
import EditeurReseaux, { type LigneReseau } from './EditeurReseaux';

/**
 * Site et réseaux du sponsor, modifiables à tout moment.
 *
 * Une présence en ligne change en dehors de toute souscription : refonte du
 * site, compte Instagram ouvert en mars, page Facebook abandonnée. Attendre le
 * prochain parrainage pour la corriger laisserait des liens morts sur le
 * livret et dans l'annuaire pendant un an.
 *
 * Le bloc n'apparaît que si le compte porte une fiche sponsor.
 */
export default function MonSiteSponsor() {
  const queryClient = useQueryClient();
  const [edition, setEdition] = useState(false);
  const [adresse, setAdresse] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['mon-dossier-sponsor'],
    queryFn: fetchMonDossierSponsor,
    retry: false,
  });

  const connu = data?.connu === true;
  const siteWeb: string | null = data?.coordonnees?.siteWeb ?? null;

  useEffect(() => {
    setAdresse(siteWeb ?? '');
  }, [siteWeb]);

  const rafraichir = () =>
    queryClient.invalidateQueries({ queryKey: ['mon-dossier-sponsor'] });

  const enregistrerSite = useMutation({
    mutationFn: () => enregistrerSiteWebSponsor(adresse.trim()),
    onSuccess: (d: any) => {
      setNotice(d?.message ?? 'Votre site est à jour.');
      setErreur(null);
      setEdition(false);
      rafraichir();
    },
    onError: (e: Error) => {
      setErreur(e.message);
      setNotice(null);
    },
  });

  const enregistrerReseaux = useMutation({
    mutationFn: (liste: LigneReseau[]) => enregistrerReseauxSponsor(liste),
    onSuccess: (d: any) => {
      setNotice(d?.message ?? 'Vos réseaux sont à jour.');
      setErreur(null);
      rafraichir();
    },
    onError: (e: Error) => {
      setErreur(e.message);
      setNotice(null);
      // La liste affichée redevient celle du serveur : garder à l'écran un
      // état refusé laisserait croire qu'il est enregistré.
      rafraichir();
    },
  });

  if (isPending || !connu) return null;

  return (
    <section className="card lg:p-5 space-y-4">
      <h2 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
        <Globe className="w-5 h-5 text-primary-600" />
        Mon site web & mes réseaux sociaux
      </h2>

      {notice && (
        <p className="text-sm rounded-xl px-3 py-2 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          {notice}
        </p>
      )}
      {erreur && <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>}

      {/* Site web */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Site web</p>
          {!edition && (
            <button
              type="button"
              onClick={() => setEdition(true)}
              className="shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-300 hover:text-primary-700"
            >
              <Pencil className="w-3.5 h-3.5" />
              {siteWeb ? 'Modifier' : 'Ajouter'}
            </button>
          )}
        </div>

        {!edition ? (
          siteWeb ? (
            <a
              href={siteWeb}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary-700 dark:text-primary-400 hover:underline inline-flex items-center gap-1 break-all"
            >
              {siteWeb}
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Aucun site renseigné. Il figurera sur le livret et dans l'annuaire des sponsors.
            </p>
          )
        ) : (
          <div className="space-y-2">
            <input
              className="input"
              type="url"
              inputMode="url"
              placeholder="www.mon-entreprise.fr"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Le « https:// » est ajouté tout seul. Laissez vide pour retirer votre site.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => enregistrerSite.mutate()}
                disabled={enregistrerSite.isPending}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {enregistrerSite.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Enregistrer
              </button>
              <button
                onClick={() => {
                  setAdresse(siteWeb ?? '');
                  setEdition(false);
                  setErreur(null);
                }}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Réseaux sociaux */}
      <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Réseaux sociaux</p>

        <EditeurReseaux
          reseaux={(data?.reseaux ?? []).map((r: any) => ({ reseau: r.reseau, url: r.url }))}
          catalogue={data?.catalogueReseaux ?? []}
          onEnregistrer={(liste) => enregistrerReseaux.mutate(liste)}
          enCours={enregistrerReseaux.isPending}
          messageVide="Aucun réseau renseigné. Ils figureront sous votre fiche sur la page des sponsors."
        />
      </div>
    </section>
  );
}
